import React, { useEffect, useState } from "react";
import PatientService from "../../../Services/PatientService";
import dayjs from "dayjs";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Bar,
  BarChart,
} from "recharts";
import { Modal, Button } from "antd";
import { RiFootprintFill } from "react-icons/ri";

const StepData = ({ smartWatchId }) => {
  const [step, setStep] = useState([]);
  const patJson = localStorage.getItem("patientData");
  const patObj = patJson ? JSON.parse(patJson) : null;
  const [loading, setLoading] = useState(false);
  const [stepDetails, setStepDetails] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (smartWatchId) {
      fetchStepFromDailies();
    }
  }, [smartWatchId]);

  const fetchStepFromDailies = async (fromDate, toDate) => {
    const start = fromDate
      ? dayjs(fromDate).format("DD/MM/YYYY")
      : dayjs().format("DD/MM/YYYY");
    const end = toDate
      ? dayjs(toDate).format("DD/MM/YYYY")
      : dayjs().format("DD/MM/YYYY");
    setLoading(true);
    try {
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: start,
        toDate: end,
        type: "DAILIES",
      });

      if (res?.data.length > 0) {
        const parsedData = res.data.map((item) => {
          let raw = {};
          try {
            raw = JSON.parse(item.RAW_JSON || "{}");
          } catch {}
          return raw;
        });
        setStep(parsedData);
        setStepDetails(parsedData[0]);
      } else {
        setStep([]);
        setStepDetails({});
      }
    } catch (err) {
      console.error("Error fetching Garmin data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setIsModalOpen(false);
  };

  const showGraph = () => {
    setIsModalOpen(true);
  };

  const steps = stepDetails?.steps ?? 0;
  const stepsGoal = stepDetails?.stepGoal ?? 7500;

  return (
    <div className="relative" onClick={showGraph}>
      {loading && <Loader />}

      <div className="bg-white shadow rounded-lg p-4 w-full h-40 cursor-pointer hover:shadow-lg transition">
        <div className="flex items-center gap-2 text-sm font-sans font-semibold">
          <RiFootprintFill className="text-orange-500 w-5 h-5" />
          <span>Steps</span>
        </div>
        <div className="flex justify-center items-center gap-3 mt-4">
          <div className="relative w-20 h-20">
            <svg className="w-full h-full" viewBox="0 0 36 36">
              <path
                className="stroke-gray-200"
                strokeWidth="3"
                fill="none"
                d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
              />
              <path
                className="stroke-blue-500"
                strokeWidth="3"
                strokeDasharray={`${(steps / stepsGoal) * 100}, 100`}
                strokeLinecap="round"
                fill="none"
                d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
              {steps}
            </span>
          </div>
          <p className="text-xs text-center mt-2">{stepsGoal}</p>
        </div>
      </div>

      {smartWatchId && (
        <StepModal
          open={isModalOpen}
          onClose={handleClose}
          smartWatchId={smartWatchId}
        />
      )}
    </div>
  );
};

const StepModal = ({ open, onClose, smartWatchId }) => {
  const [tab, setTab] = useState("1D");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  // 1D navigation
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [dailyStep, setDailyStep] = useState([]);

  const fetch1DData = async (date) => {
    setLoading(true);
    try {
      const formatted = date.format("DD/MM/YYYY");
      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 1,
        offset: 0,
        fromDate: formatted,
        toDate: formatted,
        type: "DAILIES",
      });
      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");
        const distanceInKM = res.data[0].distanceInKM ?? 0;

        setChartData([{ ...raw, distanceInKM }]);
      } else {
        setChartData([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyTimeline = async (date) => {
    if (!smartWatchId) return;
    try {
      const formatted = date.format("DD/MM/YYYY");
      const res = await PatientService.DayStepDetails({
        type: "EPOCHS",
        activityType: "WALKING",
        userId: smartWatchId,
        fromDate: formatted,
        toDate: formatted,
      });
      const formattedData = res?.data?.map((d) => ({
        ...d,
        TIME: dayjs(d.TIME, "HH:mm").format("hh:mm A"),
      }));
      setDailyStep(formattedData || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (tab === "1D") {
      fetch1DData(selectedDate);
      fetchDailyTimeline(selectedDate);
    }
  }, [tab, selectedDate, smartWatchId]);

  // Navigate previous day
  const handlePrevDay = () => {
    const prev = selectedDate.subtract(1, "day");
    setSelectedDate(prev);
  };

  const handleNextDay = () => {
    const today = dayjs();
    const next = selectedDate.add(1, "day");
    if (next.isAfter(today, "day")) return;
    setSelectedDate(next);
  };

  const [startDate, setStartDate] = useState(dayjs().subtract(6, "day"));
  const [endDate, setEndDate] = useState(dayjs());

  const fetchStepDataByRange = async (from, to) => {
    setLoading(true);
    try {
      const start = from.format("DD/MM/YYYY");
      const end = to.format("DD/MM/YYYY");

      const res = await PatientService.GetGarminDevices({
        userId: smartWatchId,
        limit: 100,
        offset: 0,
        fromDate: start,
        toDate: end,
        type: "DAILIES",
      });

      if (res?.data?.length > 0) {
        const parsed = res.data.map((item) => {
          let raw = {};
          try {
            raw = JSON.parse(item.RAW_JSON || "{}");
          } catch {}
          return {
            date: raw.calendarDate,
            steps: raw.steps || 0,
            goal: raw.stepGoal || 7500,
            distanceInKM: item.distanceInKM ?? 0,
          };
        });
        parsed.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
        const withRemaining = parsed.map((item) => ({
          ...item,
          remaining: Math.max(item.goal - item.steps, 0),
        }));
        setChartData(withRemaining);
      } else {
        setChartData([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Prev / Next for 7D
  const handlePrev7D = () => {
    const newStart = startDate.subtract(7, "day");
    const newEnd = endDate.subtract(7, "day");
    setStartDate(newStart);
    setEndDate(newEnd);
    fetchStepDataByRange(newStart, newEnd);
  };

  const handleNext7D = () => {
    const today = dayjs();
    let newStart = startDate.add(7, "day");
    let newEnd = endDate.add(7, "day");

    if (newEnd.isAfter(today, "day")) {
      newEnd = today;
      newStart = today.subtract(6, "day");
    }

    setStartDate(newStart);
    setEndDate(newEnd);
    fetchStepDataByRange(newStart, newEnd);
  };

  // Reset 7D window on tab change
  useEffect(() => {
    if (tab === "1D") {
      setSelectedDate(dayjs());
      fetch1DData(dayjs());
      fetchDailyTimeline(dayjs());
    } else if (tab === "7D") {
      const start = dayjs().subtract(6, "day");
      const end = dayjs();
      setStartDate(start);
      setEndDate(end);
      fetchStepDataByRange(start, end);
    }
  }, [tab, smartWatchId]);

  console.log("ChartData", chartData);

  const steps = chartData?.[0]?.steps ?? 0;
  const stepsGoal = chartData?.[0]?.stepGoal ?? chartData?.[0]?.goal ?? 7500;
  const distanceInKM = chartData?.[0]?.distanceInKM ?? 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      title="Steps Summary"
    >
      <div className="relative">
        {loading && <Loader />}

        <div className="flex justify-center mb-4 gap-3">
          <button
            onClick={() => setTab("1D")}
            className={`${
              tab === "1D" ? "bg-brand-primaryColor text-white" : "bg-white"
            } px-4 py-2 rounded-md`}
          >
            1D
          </button>
          <button
            className={`${
              tab === "1D" ? "bg-white" : "bg-brand-primaryColor text-white"
            } px-4 py-2 rounded-md`}
            onClick={() => setTab("7D")}
          >
            7D
          </button>
        </div>

        {tab === "1D" && (
          <div className="flex items-center justify-between mb-4">
            <button
              className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
              onClick={handlePrevDay}
            >
              ← Prev Day
            </button>
            <span className="text-sm font-medium">
              {selectedDate.format("DD/MM/YYYY")}
            </span>
            <button
              className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
              onClick={handleNextDay}
            >
              Next Day →
            </button>
          </div>
        )}

        {tab === "7D" && (
          <div className="flex justify-between mb-4">
            <button
              className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
              onClick={handlePrev7D}
            >
              ← Prev 7 Days
            </button>
            <span>
              {startDate.format("DD/MM/YYYY")} - {endDate.format("DD/MM/YYYY")}
            </span>
            <button
              className="px-4 py-2 rounded-md bg-brand-primaryColor text-white"
              onClick={handleNext7D}
            >
              Next 7 Days →
            </button>
          </div>
        )}

        {tab === "1D" ? (
          <div className="flex flex-col items-center justify-center mt-6">
            {/* Circular progress */}
            <div className="relative w-36 h-36 mb-4">
              <svg className="w-full h-full" viewBox="0 0 36 36">
                <path
                  className="stroke-gray-200"
                  strokeWidth="4"
                  fill="none"
                  d="M18 2.0845
             a15.9155 15.9155 0 1 1 0 31.831
             a15.9155 15.9155 0 1 1 0-31.831"
                />
                <path
                  className=""
                  strokeWidth="4"
                  strokeDasharray={`${(steps / stepsGoal) * 100}, 100`}
                  strokeLinecap="round"
                  fill="none"
                  stroke={steps >= stepsGoal ? "#22c55e" : "#f97316"} // green if goal reached, orange otherwise
                  d="M18 2.0845
     a15.9155 15.9155 0 1 1 0 31.831
     a15.9155 15.9155 0 1 1 0-31.831"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-semibold">{steps}</span>
                <span className="text-xs text-gray-500">steps</span>
              </div>
            </div>

            {/* Step details */}
            <div className="grid grid-cols-3 gap-4 text-center text-sm mb-4">
              <div>
                <p className="text-blue-600 font-semibold">{stepsGoal}</p>
                <p className="text-gray-500 text-xs">Goal</p>
              </div>
              <div>
                <p className="text-blue-600 font-semibold">{distanceInKM} km</p>
                <p className="text-gray-500 text-xs">Distance</p>
              </div>
              <div>
                <p className="text-orange-500 font-semibold">
                  {Math.max(stepsGoal - steps, 0)}
                </p>
                <p className="text-gray-500 text-xs">Remaining</p>
              </div>
            </div>

            <div style={{ width: "100%", height: 300 }}>
              <h2>Daily Step Timeline</h2>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={dailyStep}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <XAxis dataKey="TIME" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="STEPS"
                    stroke="#1976d2"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => dayjs(d).format("DD/MM")}
                />
                <YAxis />
                <Tooltip
                  formatter={(value, name, entry) => {
                    if (name === "steps") return [`${value}`, "Steps"];
                    if (name === "remaining") return [`${value}`, "Remaining"];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar
                  dataKey="steps"
                  stackId="a"
                  fill="#36a2eb"
                  name="Steps"
                  radius={[2, 2, 0, 0]}
                  shape={(props) => {
                    const { x, y, width, height, payload } = props;
                    const goalReached = payload.steps >= payload.goal;
                    return (
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={goalReached ? "#22c55e" : "#36a2eb"}
                        rx={2}
                      />
                    );
                  }}
                />
                <Bar
                  dataKey="remaining"
                  stackId="a"
                  fill="#e0e0e0"
                  name="Remaining to Goal"
                />
              </BarChart>
            </ResponsiveContainer>

            {/* Summary cards */}
            <div className="flex justify-around mt-5 text-center flex-wrap gap-3">
              <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
                <p className="text-blue-600 font-semibold text-base">
                  {chartData.reduce((a, b) => a + (b.steps || 0), 0)}
                </p>
                <p className="text-gray-500 text-xs">Total Steps</p>
              </div>
              <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
                <p className="text-blue-600 font-semibold text-base">
                  {chartData
                    .reduce((a, b) => a + (b.distanceInKM || 0), 0)
                    .toFixed(2)}
                </p>
                <p className="text-gray-500 text-xs">Total Dist (km)</p>
              </div>
              <div className="bg-gray-50 shadow-sm rounded-xl p-3 w-28">
                <p className="text-blue-600 font-semibold text-base">
                  {(
                    chartData.reduce((a, b) => a + (b.steps || 0), 0) /
                    (chartData.length || 1)
                  ).toFixed(0)}
                </p>
                <p className="text-gray-500 text-xs">Avg Daily</p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3 mb-5 mt-1">
              {chartData.map((item, idx) => {
                const dayName = dayjs(item.date).format("dddd");
                const steps = item.steps || 0;
                const goal = item.goal || 7500;
                const distance = item.distanceInKM || 0;
                const progressPercent = Math.min((steps / goal) * 100, 100);
                const goalReached = steps >= goal;

                return (
                  <div
                    key={idx}
                    className="bg-white shadow mt-2 rounded-lg p-3 flex flex-col items-center gap-2"
                  >
                    <p className="text-xs text-gray-500">{dayName}</p>

                    {/* Horizontal layout */}
                    <div className="flex items-center gap-3">
                      <div className="relative w-16 h-16">
                        <svg className="w-full h-full" viewBox="0 0 36 36">
                          <path
                            className="stroke-gray-200"
                            strokeWidth="3"
                            fill="none"
                            d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
                          />
                          <path
                            stroke={goalReached ? "#22c55e" : "#36a2eb"}
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeDasharray={`${progressPercent}, 100`}
                            fill="none"
                            d="M18 2.0845a15.9155 15.9155 0 1 1 0 31.831 15.9155 15.9155 0 1 1 0-31.831"
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-xs font-semibold">
                          <span>{steps}</span>
                          <span className="text-gray-400">/{goal}</span>
                        </div>
                      </div>

                      {/* Distance info next to the circle */}
                      <div className="flex flex-col items-start justify-center gap-1">
                        <p className="text-sm font-semibold text-blue-600">
                          {distance.toFixed(2)} km
                        </p>
                        <p className="text-xs text-gray-500">Distance</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const Loader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-50">
    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export default StepData;
